import React, { useState, useMemo } from 'react';
import Header from './Header';
import CategoryTabs from './CategoryTabs';
import ProductDetail from './ProductDetail';
import { ProductGrid, ProductList } from './ProductLayouts';
import { FeaturesSection, StatsSection, PricingSection, FAQSection, TestimonialStrip, Footer } from './Sections';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Toaster, toast } from 'sonner';
import { CircleUser, MessageCircle, Loader2, ShoppingCart, Minus, Plus } from 'lucide-react';
import { useSite } from '../lib/siteContext';
import { PaymentModal } from './PaymentModal';
import AuthDialog from './AuthDialog';
import MyAccountDialog from './MyAccountDialog';
import ResetPasswordDialog from './ResetPasswordDialog';
import VerifyEmailDialog from './VerifyEmailDialog';
import { useCustomer } from '../lib/customerContext';

const CART_KEY = 'cart_v1';

const HomePage = () => {
  const { data, loading, error } = useSite();
  const { customer, logout } = useCustomer();
  const [activeCategory, setActiveCategory] = useState(null);
  const [gsm, setGsm] = useState('240');
  const [cart, setCart] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(CART_KEY));
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  const [loginOpen, setLoginOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [resetToken, setResetToken] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('reset_token') || ''; } catch { return ''; }
  });
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [cartPayOpen, setCartPayOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  // Gate checkout: must be logged in AND email-verified to place an order
  const handleProceedToCheckout = () => {
    if (!customer) {
      toast.info('Order place karne ke liye pehle login ya signup karein');
      setLoginOpen(true);
      return;
    }
    if (!customer.email_verified) {
      setVerifyOpen(true);
      return;
    }
    setCartPayOpen(true);
  };

  // Persist cart so it survives refresh / navigation
  React.useEffect(() => {
    try { localStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch { /* storage unavailable */ }
  }, [cart]);

  const didInitProduct = React.useRef(false);

  // Set initial category from data once loaded; deep-link to ?product=<id> if present
  React.useEffect(() => {
    if (data?.categories?.length && !activeCategory) {
      let pid = '';
      try { pid = new URLSearchParams(window.location.search).get('product') || ''; } catch { pid = ''; }
      const linked = pid && data.products?.find((x) => x.id === pid);
      didInitProduct.current = true;
      if (linked) {
        setActiveCategory(linked.category_slug);
        if (linked.gsm) setGsm(String(linked.gsm));
        setSelectedProductId(linked.id);
        return;
      }
      const first = data.categories[0];
      setActiveCategory(first.slug);
      if (first.has_gsm && first.gsm_options?.length) setGsm(first.gsm_options[0]);
    }
  }, [data, activeCategory]);

  // Keep URL in sync with the open product so each product has a shareable link
  React.useEffect(() => {
    if (!didInitProduct.current) return;
    try {
      const url = new URL(window.location.href);
      if (selectedProductId) url.searchParams.set('product', selectedProductId);
      else url.searchParams.delete('product');
      window.history.replaceState({}, '', url.pathname + url.search);
    } catch { /* history unavailable */ }
  }, [selectedProductId]);

  const productsForCat = useMemo(() => {
    if (!data?.products) return [];
    const inCat = data.products.filter((p) => p.category_slug === activeCategory);
    const cat = (data.categories || []).find((c) => c.slug === activeCategory);
    if (cat?.has_gsm && gsm) {
      return inCat.filter((p) => !p.gsm || String(p.gsm) === String(gsm));
    }
    return inCat;
  }, [data, activeCategory, gsm]);

  // Category tab click: switch category, pick its first GSM, clear open product
  const handleCategoryChange = (slug) => {
    setSelectedProductId(null);
    setActiveCategory(slug);
    const cat = (data?.categories || []).find((c) => c.slug === slug);
    if (cat?.has_gsm && cat.gsm_options?.length) {
      setGsm(cat.gsm_options[0]);
    }
  };

  // GSM switch also clears the open product so the filtered grid shows
  const handleGsmChange = (g) => {
    setSelectedProductId(null);
    setGsm(g);
  };

  // Open a specific product (used by header buttons mapped to a product)
  const openProduct = (productId) => {
    const p = data?.products?.find((x) => x.id === productId);
    if (!p) return;
    setActiveCategory(p.category_slug);
    setSelectedProductId(productId);
    setTimeout(() => {
      document.getElementById('top')?.scrollIntoView({ behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 50);
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="flex items-center gap-2 text-neutral-600">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading store...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center bg-white p-6 text-center">
        <div>
          <h2 className="text-lg font-bold text-red-600 mb-2">Failed to load</h2>
          <p className="text-sm text-neutral-600">{error || 'Backend not reachable'}</p>
        </div>
      </div>
    );
  }

  const { branding, nav_links, banners, categories, features, stats, faqs, pricing, page_settings } = data;
  const settings = page_settings || { product_layout: 'detail', show_live_badge: true, sections: [] };
  const layout = settings.product_layout || 'detail';

  // Get visible sections sorted by order
  const orderedSections = [...(settings.sections || [])]
    .filter((s) => s.visible)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const addToCart = (o) => setCart((p) => [...p, o]);
  const removeFromCart = (index) => setCart((p) => p.filter((_, i) => i !== index));
  const updateCartQty = (index, delta) => setCart((p) => p.map((o, i) => {
    if (i !== index) return o;
    const floor = o.minQty || 1;
    const newQty = (o.totalQty || 0) + delta;
    if (newQty < floor) {
      toast.error(`Minimum order for ${o.productName} is ${floor} pcs`);
      return o;
    }
    return { ...o, totalQty: newQty, totalPrice: newQty * (o.price || 0) };
  }));

  const cartTotalQty = cart.reduce((a, o) => a + (o.totalQty || 0), 0);
  const cartTotalPrice = cart.reduce((a, o) => a + (o.totalPrice || 0), 0);
  const cartItems = cart.flatMap((o) => (o.items || []).map((i) => ({ ...i, product: o.productName })));
  const cartProductLabel = cart.length === 1 ? cart[0].productName : `${cart.length} products`;
  const cartAvgPrice = cartTotalQty > 0 ? Math.round(cartTotalPrice / cartTotalQty) : 0;

  const renderSection = (section) => {
    switch (section.key) {
      case 'product':
        if (selectedProductId) {
          return (
            <ProductDetail
              key={`${section.key}-${selectedProductId}`}
              products={data.products}
              activeCategory={activeCategory}
              selectedProductId={selectedProductId}
              gsm={gsm}
              onAddToCart={addToCart}
              onBack={() => setSelectedProductId(null)}
            />
          );
        }
        if (layout === 'grid') {
          return <ProductGrid key={section.key} products={productsForCat} onSelectProduct={setSelectedProductId} />;
        }
        if (layout === 'list') {
          return <ProductList key={section.key} products={productsForCat} onSelectProduct={setSelectedProductId} />;
        }
        return (
          <ProductGrid
            key={section.key}
            products={productsForCat}
            onSelectProduct={setSelectedProductId}
          />
        );
      case 'testimonials':
        return <TestimonialStrip key={section.key} />;
      case 'features':
        return <FeaturesSection key={section.key} features={features} heroQuote={branding?.hero_quote} title={section.title} />;
      case 'stats':
        return <StatsSection key={section.key} stats={stats} />;
      case 'pricing':
        return <PricingSection key={section.key} pricing={pricing} title={section.title} />;
      case 'faqs':
        return <FAQSection key={section.key} faqs={faqs} title={section.title} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Toaster position="top-center" richColors />
      <Header onLoginClick={() => setLoginOpen(true)} branding={branding} navLinks={nav_links} banners={banners} onSelectProduct={openProduct} cartCount={cart.length} onOrderNow={() => document.getElementById('cart-summary')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} customer={customer} onLogout={logout} onAccountClick={() => setAccountOpen(true)} />
      <CategoryTabs categories={categories} active={activeCategory} gsm={gsm} onChange={handleCategoryChange} onGsmChange={handleGsmChange} />

      <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2">
        {cart.length > 0 && (
          <button
            data-testid="floating-view-cart-button"
            onClick={() => document.getElementById('cart-summary')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="flex items-center gap-1.5 px-3.5 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-full shadow-lg text-[12px] font-semibold animate-in fade-in slide-in-from-bottom-2"
          >
            <ShoppingCart className="w-4 h-4" /> View Cart ({cart.reduce((s, c) => s + (c.totalQty || 0), 0)})
          </button>
        )}
        <div id="floating-actions-slot" className="flex flex-col items-end empty:hidden" />
        {settings.show_live_badge && (
          <a data-testid="whatsapp-us-button" href="https://wa.me/919971890401" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-full shadow-lg text-[12px] font-semibold">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp Us
          </a>
        )}
      </div>

      <main>
        {orderedSections.map((s) => renderSection(s))}
      </main>

      <section id="cart-summary" className="px-3 md:px-6 py-6 border-t border-neutral-200 bg-neutral-50">
        <div className="max-w-md mx-auto border border-neutral-200 rounded-lg bg-white overflow-hidden">
          <div className="px-3 py-2 bg-neutral-900 text-white flex items-center gap-2">
            <ShoppingCart className="w-4 h-4" />
            <span className="text-[13px] font-semibold">Your Cart</span>
          </div>
          <div className="p-3">
            {cart.length === 0 ? (
              <div className="text-center py-8 text-[13px] text-neutral-500">
                <ShoppingCart className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                Your cart is empty
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {cart.map((o, i) => (
                    <div key={i} className="flex items-center justify-between text-[13px] py-1.5 border-b border-neutral-100">
                      <span className="text-neutral-700 truncate pr-2">{o.productName}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="flex items-center border border-neutral-200 rounded overflow-hidden bg-white">
                          <button data-testid={`cart-summary-${i}-decrease-button`} onClick={() => updateCartQty(i, -1)} className="w-6 h-6 grid place-items-center text-neutral-500 hover:bg-neutral-100">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-10 text-center">{o.totalQty} pcs</span>
                          <button data-testid={`cart-summary-${i}-increase-button`} onClick={() => updateCartQty(i, 1)} className="w-6 h-6 grid place-items-center text-neutral-500 hover:bg-neutral-100">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="font-medium">₹{o.totalPrice.toLocaleString('en-IN')}</span>
                        <button data-testid={`cart-summary-remove-${i}`} onClick={() => removeFromCart(i)} className="text-neutral-400 hover:text-red-600">✕</button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-[14px] pt-2 border-t border-neutral-200">
                  <span className="font-semibold">Cart Total</span>
                  <span className="font-bold text-orange-600">₹{cartTotalPrice.toLocaleString('en-IN')}</span>
                </div>
                <Button data-testid="cart-summary-proceed-to-checkout-button" onClick={handleProceedToCheckout} className="w-full bg-orange-600 hover:bg-orange-700 text-white h-9">
                  Proceed to Checkout ❯
                </Button>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer branding={branding} />

      <AuthDialog open={loginOpen} onOpenChange={setLoginOpen} />
      <ResetPasswordDialog
        open={!!resetToken}
        token={resetToken}
        onOpenChange={(v) => { if (!v) { setResetToken(''); try { window.history.replaceState({}, '', window.location.pathname); } catch (e) {} } }}
        onDone={() => setLoginOpen(true)}
      />
      <MyAccountDialog open={accountOpen} onOpenChange={setAccountOpen} whatsappNumber={(branding?.whatsapp_number || '').replace(/[^0-9]/g, '')} />
      <VerifyEmailDialog open={verifyOpen} onOpenChange={setVerifyOpen} onVerified={() => setCartPayOpen(true)} />

      {cartPayOpen && (
        <PaymentModal
          open={cartPayOpen}
          onClose={() => setCartPayOpen(false)}
          onOrderSent={() => setCart([])}
          branding={branding}
          items={cartItems}
          totalQty={cartTotalQty}
          price={cartAvgPrice}
          total={cartTotalPrice}
          productName={cartProductLabel}
        />
      )}
    </div>
  );
};

export default HomePage;
